import $ from 'jquery';
import * as React from 'react';

import { getInternalFromNativeDate, getNativeFromInternalDate } from 'helper/date';
import noop from 'helper/noop';

import { MOBILE_FIELD_TYPE } from 'types/MobileFieldType';
import { MobileFieldTypeEnumType } from 'types/MobileFieldType';

export type FieldInputProps = {
	state?: string;
	id?: string;
	onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
	onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
	onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
	value: string;
	name: string;
	placeholder?: string;
	unit?: string;
	message?: string;
	isValidating?: boolean;
	modifier?: string;
	disabled?: boolean;
	autoComplete?: boolean;
	mobileFieldType?: MobileFieldTypeEnumType;
};

export type MobileInputTypeSpecificPropsType = {
	type: string;
	autoComplete?: 'on' | 'off';
	inputMode?: 'none' | 'text' | 'tel' | 'url' | 'email' | 'numeric' | 'decimal' | 'search';
	min?: any;
	noValidate?: boolean;
	pattern?: string;
	step?: string;
};

class FieldInput extends React.Component<FieldInputProps> {
	public static displayName = 'FieldInput';

	public refMobileField?: HTMLInputElement;

	// onFocus origin -> focus mobileField
	public handleFocusField = (event: React.FocusEvent<HTMLInputElement>) => {
		const { mobileFieldType, onFocus = noop } = this.props;
		if (mobileFieldType) {
			event.preventDefault();
			event.stopPropagation();
			if (this.refMobileField) {
				$(this.refMobileField).focus();
			}
			return;
		}

		onFocus(event);
	};

	public handleChangeMobileField = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (!(event.target instanceof HTMLInputElement)) {
			return;
		}

		const value: string = event.target.value || '';

		const targetClone: any = event.target.cloneNode(true);
		targetClone.type = 'text';
		targetClone.value = this.getInternalFromNativeValue(value);

		this.props.onChange({
			...event,
			target: targetClone,
		});
	};

	public getNativeFromInternalValue = (value: string): string => {
		switch (this.props.mobileFieldType) {
			case MOBILE_FIELD_TYPE.date:
				return getNativeFromInternalDate(value);
			default: {
				return value;
			}
		}
	};

	public getInternalFromNativeValue = (value: string): string => {
		switch (this.props.mobileFieldType) {
			case MOBILE_FIELD_TYPE.date:
				return getInternalFromNativeDate(value);
			default: {
				return value;
			}
		}
	};

	get mobileInputTypeSpecificProps(): MobileInputTypeSpecificPropsType {
		switch (this.props.mobileFieldType) {
			case MOBILE_FIELD_TYPE.date: {
				return {
					type: 'date',
					noValidate: true,
					autoComplete: 'off',
				};
			}
			case MOBILE_FIELD_TYPE.integer: {
				return {
					type: 'text',
					pattern: '\\d*',
					inputMode: 'numeric',
					min: '0',
					noValidate: true,
					autoComplete: 'off',
				};
			}
			default: {
				return {
					type: 'text',
					noValidate: true,
				};
			}
		}
	}

	get className(): string {
		const { disabled, modifier, state } = this.props;
		const classes: Array<string> = ['a-field-text'];
		if (modifier) {
			classes.push(`a-field-text--${modifier}`);
		}
		if (state) {
			classes.push(`state-a-field-text--${state}`);
		}
		if (disabled) {
			classes.push(`state-a-field-text--disabled`);
		}
		return classes.join(' ');
	}

	get elementMessage() {
		const { message, isValidating = false } = this.props;
		if (message && !isValidating) {
			return <div className="a-field-text__message">{message}</div>;
		}
		return null;
	}

	get elementLabel() {
		const { id } = this.props;
		if (id) {
			return (
				<label className="a-field-text__label" htmlFor={id}>
					{id}
				</label>
			);
		}
		return null;
	}

	get elementUnit() {
		const { unit } = this.props;
		if (unit) {
			return <span className="a-field-text__unit">{unit}</span>;
		}
		return null;
	}

	get autoComplete() {
		return typeof this.props.autoComplete === 'boolean' && this.props.autoComplete === false ? 'off' : undefined;
	}

	get elementField() {
		const {
			id,
			onChange,
			value,
			onFocus = noop,
			onBlur = noop,
			name,
			placeholder,
			mobileFieldType,
			disabled = false,
		} = this.props;

		let props = {
			id,
			value,
			onChange,
			onFocus,
			onBlur,
			name,
			placeholder,
			disabled,
			autoComplete: this.autoComplete,
		};

		if (mobileFieldType === MOBILE_FIELD_TYPE.date) {
			props.id = id ? `${id}-origin` : undefined;
			props.onFocus = this.handleFocusField;
			props.onBlur = undefined as any;
			props.name = `${name}-origin`;
		} else if (mobileFieldType) {
			props = { ...props, ...this.mobileInputTypeSpecificProps };
		}

		return <input className="a-field-text__input" type="text" {...(props as any)} />;
	}

	get elementFieldMobile() {
		const { id, value, onFocus = noop, onBlur = noop, name, placeholder, disabled = false } = this.props;
		return (
			<input
				ref={(input) => (this.refMobileField = input!)}
				className="a-field-text__input a-field-text__input--invisible-field"
				id={id}
				value={this.getNativeFromInternalValue(value)}
				onChange={this.handleChangeMobileField}
				onFocus={onFocus}
				onBlur={onBlur}
				name={name}
				placeholder={placeholder}
				disabled={disabled}
				autoComplete={this.autoComplete}
				{...this.mobileInputTypeSpecificProps}
			/>
		);
	}

	public render() {
		const { mobileFieldType } = this.props;

		return (
			<div className={this.className}>
				{this.elementLabel}
				<div className="a-field-text__wrap">
					{this.elementField}
					{this.elementUnit}
				</div>
				{mobileFieldType === MOBILE_FIELD_TYPE.date && (
					<div className="a-field-text__wrap a-field-text__wrap--invisible-field">
						{this.elementFieldMobile}
						{this.elementUnit}
					</div>
				)}
				{this.elementMessage}
			</div>
		);
	}
}

export default FieldInput;
